import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { Spinner, Center, VStack, Text, Button, Link, Icon } from '@chakra-ui/react';
import { WifiOff } from 'lucide-react';
import { useOnboardingStatus, canGatedMemberAccessPath } from '@/api/queries/useOnboardingQueries';
import { guardUnavailableCopy } from '@/copy';
import { OnboardingTour } from './OnboardingTour';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

/**
 * OnboardingGuard component that checks if the user needs to complete onboarding.
 *
 * Redirects through the onboarding flow:
 * 1. /onboarding/permissions - Request location and notification permissions
 * 2. /onboarding/profile - Profile setup (photo, bio)
 * 3. /onboarding/friends - Find and follow friends
 * 4. /onboarding/quiz - Take Quiz 1 (or straight to /quizzes/level2 if level 1 is
 *    already done — see the gate section below)
 * 5. Shows guided tour overlay
 *
 * New users go through the full flow. Returning users (who've taken the quiz before)
 * skip directly to the tour when a new tour version is available.
 *
 * D43a gate (FR-A1, FR-A2): quizzes 1 and 2 are both required before the member
 * is released. The gate's redirect target and its pass-through rule both derive
 * from the server-served `nextRequiredQuizKey` via the shared
 * `canGatedMemberAccessPath` predicate — the same predicate the Menu page and
 * the tab bar use, so the three surfaces cannot disagree about where a gated
 * member may be.
 *
 * D43a failure policy (FR-A4): the status check fails closed, not open. An
 * absent status (no prior successful fetch cached) renders the "unavailable"
 * panel below rather than releasing the member into the app — the member
 * gets a Retry control and a route to /menu (so logout stays reachable)
 * instead of a silent bypass. A status query that errors but still has
 * cached data from a prior success is NOT treated as absent — both the
 * render path and the redirect effect fall through to the gate logic using
 * that cached data.
 */
export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: onboardingStatus, isLoading, isFetching, errorUpdatedAt, refetch } =
    useOnboardingStatus();
  const [showTour, setShowTour] = useState(false);
  const [tourDismissed, setTourDismissed] = useState(false);

  // The gate line's own literal fallback ('level1') only ever applies to
  // legacy/test cache shapes that predate the nextRequiredQuizKey field —
  // real server responses always carry it whenever needsQuiz is true.
  const gateRequiredQuizKey = (status: typeof onboardingStatus) =>
    status?.nextRequiredQuizKey ?? (status?.needsQuiz ? 'level1' : null);

  useEffect(() => {
    if (isLoading) return;
    if (!onboardingStatus) return;
    // Defense-in-depth: while a background refetch is in-flight (e.g. triggered by
    // an optimistic invalidateQueries after a step mutation), skip redirect logic so
    // the guard doesn't act on a potentially stale snapshot mid-transition.
    // The setQueryData in each step mutation's onSuccess already stamps the correct
    // value synchronously, so isFetching is only true during the server reconciliation
    // window — deferring the redirect until settled is safe here.
    // Note: isFetching is true during initial load too, but isLoading already guards
    // that case above; isFetching without isLoading means background refetch only.
    if (isFetching) return;

    // Onboarding flow order for NEW users:
    // 1. Permissions → 2. Profile → 3. Friends → 4. Quiz gate → 5. Tour

    // Check permissions step (new users only)
    if (onboardingStatus.needsPermissions) {
      navigate({ to: '/onboarding/permissions' });
      return;
    }

    // Check profile step (new users only)
    if (onboardingStatus.needsProfile) {
      navigate({ to: '/onboarding/profile' });
      return;
    }

    // Check friends step (new users only)
    if (onboardingStatus.needsFriends) {
      navigate({ to: '/onboarding/friends' });
      return;
    }

    // Check the quiz gate (quizzes 1 and 2)
    const requiredQuizKey = gateRequiredQuizKey(onboardingStatus);
    if (onboardingStatus.needsQuiz && requiredQuizKey) {
      if (canGatedMemberAccessPath(location.pathname, requiredQuizKey)) {
        // Allowed while gated (the required quiz itself, the quizzes list,
        // or /menu) — let the member stay here.
        return;
      }
      // FR-A5 — resuming mid-gate (level2 required) goes STRAIGHT to the
      // quiz, never back through the level1 welcome screen.
      if (requiredQuizKey === 'level1') {
        navigate({ to: '/onboarding/quiz' });
      } else {
        navigate({ to: '/quizzes/$quizKey', params: { quizKey: requiredQuizKey } });
      }
      return;
    }

    // If user needs the tour and hasn't dismissed it this session, show it
    // This applies to both new users (after the gate) and returning users (new tour version)
    if (onboardingStatus.needsTour && !tourDismissed) {
      setShowTour(true);
    }
  }, [onboardingStatus, isLoading, isFetching, navigate, location.pathname, tourDismissed]);

  const handleCloseTour = () => {
    setShowTour(false);
    setTourDismissed(true);
  };

  // Show loading spinner while checking onboarding status — but only for the
  // genuine first load, which `errorUpdatedAt === 0` is what narrows this to.
  //
  // `isLoading` is `isPending && isFetching`, and query-core resets `status` to
  // 'pending' on EVERY fetch whose query has no data yet (see `fetchState` in
  // query-core's query.ts). So once this query has settled as an error with no
  // cached data, `isLoading` flips back to true on each subsequent attempt.
  // On /menu that is self-sustaining: the fail-closed branch below renders
  // `children` for that one path, TabLayout's MainNavigationTabBar and the Menu
  // page both observe this same query, mounting them refetches it (an errored
  // query is always stale, and refetchOnMount defaults to true), the refetch
  // flips `isLoading` back on, this branch replaces them with the spinner, and
  // their observers unmount — ~4 requests/second forever, permanently stuck on
  // the spinner, with /menu (and therefore logout) never actually reachable.
  // `errorUpdatedAt` is monotonic and is not reset by a new fetch, so it is the
  // stable "has this query ever settled?" signal that neither `isLoading` nor
  // `isError` can give us here.
  if (isLoading && errorUpdatedAt === 0) {
    return (
      <Center h="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="purple.500" thickness="4px" />
          <Text color="gray.400">Loading...</Text>
        </VStack>
      </Center>
    );
  }

  // FR-A4 — fail closed: a resolved query with NO usable data at all (no
  // prior success ever cached) renders the unavailable panel. Children are
  // NOT rendered — this is deliberately not the same as a loading state,
  // because the member needs a visible, recoverable way out (Retry, or a
  // route to /menu) rather than an indefinite spinner or a silent release
  // into the app.
  //
  // Deliberately NOT `isError || !onboardingStatus`: in TanStack Query v5,
  // `status` becomes 'error' after a failed BACKGROUND refetch even when
  // `data` is still populated from a prior success (isError and cached data
  // are not mutually exclusive). OnboardingGuard wraps the entire
  // `_authenticated` tree, so keying off `isError` alone replaced the whole
  // app with this panel for a fully-onboarded member on a transient
  // background failure (e.g. a 503 blip) — not just for a gated member with
  // no usable status at all. `!onboardingStatus` alone is the correct fail-
  // closed condition: an error WITH cached data still falls through to the
  // gate logic below using that cached data (which itself fails closed for
  // the actual gate decision), while an error with NO data at all still hits
  // this panel.
  if (!onboardingStatus) {
    // FR-A4 "recoverable, never trapped" — /menu is where the panel's own
    // secondary link points, but /menu also lives inside `_authenticated`
    // (see router/routes/menu.tsx), which OnboardingGuard wraps just like
    // every other authenticated route. Without this exemption, clicking that
    // link during a persistent outage just remounts the guard, which fails
    // the exact same way, showing the exact same panel — logout (reachable
    // from the Menu page) is never actually reachable. Render children
    // (TabLayout + Outlet -> MenuPage) directly for this one path instead.
    if (location.pathname === '/menu') {
      return <>{children}</>;
    }
    return (
      <Center h="100vh">
        <VStack spacing={4} maxW="320px" textAlign="center" aria-live="polite">
          <Icon as={WifiOff} boxSize={8} color="gray.500" aria-hidden="true" />
          <Text color="gray.300">{guardUnavailableCopy.body}</Text>
          <Button
            colorScheme="purple"
            onClick={() => refetch()}
            isLoading={isFetching}
            autoFocus
            minH="44px"
            minW="44px"
          >
            {guardUnavailableCopy.retryLabel}
          </Button>
          <Link href="/menu" color="gray.400" fontSize="sm" minH="44px" px={3} display="inline-flex" alignItems="center">
            {guardUnavailableCopy.secondaryLabel}
          </Link>
        </VStack>
      </Center>
    );
  }

  // If user needs any onboarding step, show loading while redirecting
  const requiredQuizKey = gateRequiredQuizKey(onboardingStatus);
  const needsOnboarding =
    onboardingStatus.needsPermissions ||
    onboardingStatus.needsProfile ||
    onboardingStatus.needsFriends ||
    (onboardingStatus.needsQuiz &&
      !!requiredQuizKey &&
      !canGatedMemberAccessPath(location.pathname, requiredQuizKey));

  if (needsOnboarding) {
    return (
      <Center h="100vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="purple.500" thickness="4px" data-testid="onboarding-redirect-spinner" />
          <Text color="gray.400">Setting up your experience...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <>
      {children}
      <OnboardingTour isOpen={showTour} onClose={handleCloseTour} />
    </>
  );
}

export default OnboardingGuard;
